import { createFileRoute, useNavigate, Outlet, useMatch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Calendar,
  User,
  Scale,
  CheckCircle,
  Ban,
  Clock,
  Info,
  FileText,
  Eye,
  Pencil,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useContract,
  useSelectDraft,
  useUpdateTitle,
  useDeleteContract,
} from '@/hooks/api/use-contracts';
import { useSession } from '@/lib/auth-client';
import { versionKeys } from '@/lib/query-keys';
import { usePermissions } from '@/hooks/api/use-permissions';
import {
  ContractStatusBadge,
  ContractTypeBadge,
} from '@/components/contracts/contract-status-badge';
import { ContractFormPreview } from '@/components/contracts/contract-form-preview';
import { WorkflowActionBar } from '@/components/contracts/workflow-action-bar';
import { ExportButton } from '@/components/export/export-button';
import { ExportHistory } from '@/components/export/export-history';
import { VersionTimeline } from '@/components/review/version-timeline';
import { VersionPreview } from '@/components/review/version-preview';
import { DraftSelection } from '@/components/contracts/draft-selection';
import { GenerationProgress } from '@/components/contracts/generation-progress';
import { useVersionTimeline, useCurrentVersion, useDiff } from '@/hooks/api/use-versions';
import { DiffViewer } from '@/components/review/diff-viewer';
import { useWorkflowTransition } from '@/hooks/use-workflow-transition';

export const Route = createFileRoute('/_authenticated/contracts/$contractId')({
  component: ContractDetailPage,
});

function ContractDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { contractId } = Route.useParams();

  // Poll every 3s while contract is in GENERATING state
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const {
    data: contract,
    isPending,
    isError,
  } = useContract(contractId, {
    refetchInterval: pollingEnabled ? 3000 : false,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    const isGenerating = contract?.status === 'GENERATING';
    setPollingEnabled(isGenerating);
    // When status changes from GENERATING, refetch versions to pick up new drafts
    if (!isGenerating && contract?.id) {
      void queryClient.invalidateQueries({ queryKey: versionKeys.timeline(contract.id) });
      void queryClient.invalidateQueries({ queryKey: versionKeys.current(contract.id) });
    }
  }, [contract?.status, contract?.id, queryClient]);
  const { data: session } = useSession();
  const { data: permissions } = usePermissions();
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);
  const [diffState, setDiffState] = useState<{ versionA: string; versionB: string } | null>(null);
  const selectDraft = useSelectDraft(contractId);

  // Fetch current (latest) version — always available, not filtered by timeline
  const hasContent = !!contract?.generatedContent?.length;
  const { data: currentVersion } = useCurrentVersion(contractId, { enabled: hasContent });

  // Fetch version timeline (filtered: post-review only)
  const { data: timeline } = useVersionTimeline(contractId);
  const latestTimelineVersion = timeline?.groups
    ?.flatMap((g) => g.versions)
    .sort((a, b) => b.versionNumber - a.versionNumber)[0];

  // All timeline versions for preview navigation (post-review)
  const allVersions =
    timeline?.groups
      ?.flatMap((g) => g.versions)
      .sort((a, b) => b.versionNumber - a.versionNumber) ?? [];

  const previewVersion = previewVersionId
    ? allVersions.find((v) => v.id === previewVersionId)
    : null;

  const previewIdx = previewVersionId
    ? allVersions.findIndex((v) => v.id === previewVersionId)
    : -1;

  // Inline title editing
  const updateTitle = useUpdateTitle(contractId);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [optimisticTitle, setOptimisticTitle] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Workflow transition (for regenerate from draft selection)
  const workflowTransition = useWorkflowTransition(contract);

  // Delete contract (for CANCELLED status)
  const deleteContractMutation = useDeleteContract();

  // When child route (e.g. /review) is active, render it via Outlet
  const reviewMatch = useMatch({
    from: '/_authenticated/contracts/$contractId/review',
    shouldThrow: false,
  });
  if (reviewMatch) {
    return <Outlet />;
  }

  const currentUserId = session?.user?.id ?? '';
  const currentUserRole = permissions?.role ?? '';
  const workspaceSuspended = permissions?.workspaceStatus === 'SUSPENDED';

  const isCreator = contract?.createdBy === currentUserId;
  const isAssignedLawyer = contract?.assignedLawyerId === currentUserId;
  const isEditable =
    isCreator &&
    (contract?.status === 'FORM_FILLING' ||
      contract?.status === 'DRAFT' ||
      contract?.status === 'DRAFT_SELECTION');

  function handleTitleSave() {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === contract?.title) {
      setIsEditingTitle(false);
      return;
    }
    setOptimisticTitle(trimmed);
    setIsEditingTitle(false);
    updateTitle.mutate(trimmed, {
      onSettled: () => setOptimisticTitle(null),
    });
  }

  function handleNavigateToEdit() {
    if (!contract) return;
    if (contract.contractType === 'NDA') {
      void navigate({ to: '/contracts/new-nda', search: { id: contract.id } });
    } else {
      void navigate({ to: '/contracts/new-b2b', search: { id: contract.id } });
    }
  }

  // Loading state
  if (isPending) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !contract) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <AlertCircle className="size-12 text-destructive" />
          <p className="text-sm text-muted-foreground">{t('contractDetail.loadError')}</p>
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/contracts', search: { status: undefined } })}
            className="cursor-pointer"
          >
            <ArrowLeft className="size-4" />
            {t('contractDetail.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const displayTitle = optimisticTitle ?? (contract.title || t('contractsList.untitled'));
  const dateLocale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const createdDate = new Date(contract.createdAt).toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const creatorDisplay = contract.creatorName || contract.creatorEmail;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Back navigation */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate({ to: '/contracts', search: { status: undefined } })}
        className="mb-6 cursor-pointer -ml-2"
      >
        <ArrowLeft className="size-4" />
        {t('contractDetail.backToList')}
      </Button>

      {/* Combined header + status banners + workflow — one Card */}
      <Card className="mb-8">
        <CardContent className="space-y-0 py-5">
          {/* Header row: title + badges */}
          <div className="flex flex-wrap items-center gap-3">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
                maxLength={100}
                className="text-2xl font-bold bg-transparent border-b-2 border-primary outline-none min-w-[200px] py-0"
                disabled={updateTitle.isPending}
                autoFocus
              />
            ) : (
              <h1
                className={`text-2xl font-bold border-b-2 border-transparent group/title ${isCreator ? 'cursor-pointer hover:text-primary/80 transition-colors' : ''}`}
                onClick={() => {
                  if (!isCreator) return;
                  setEditTitle(contract.title || '');
                  setIsEditingTitle(true);
                }}
                title={isCreator ? t('contractDetail.clickToRename') : undefined}
              >
                {displayTitle}
                {isCreator && (
                  <Pencil className="ml-2 inline size-4 text-muted-foreground/0 group-hover/title:text-muted-foreground transition-colors" />
                )}
              </h1>
            )}
            <span className="border-b-2 border-transparent">
              <ContractTypeBadge type={contract.contractType} />
            </span>
            <span className="border-b-2 border-transparent">
              <ContractStatusBadge status={contract.status} />
            </span>
          </div>

          {/* Metadata row */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Calendar className="size-3.5" />
              {createdDate}
            </span>
            {creatorDisplay && (
              <>
                <span className="text-muted-foreground/40">|</span>
                <span className="flex items-center gap-1.5">
                  <User className="size-3.5" />
                  {t('contractDetail.author')}: {creatorDisplay}
                </span>
              </>
            )}
            {contract.assignedLawyerId && (
              <>
                <span className="text-muted-foreground/40">|</span>
                <span className="flex items-center gap-1.5">
                  <Scale className="size-3.5" />
                  {t('contractDetail.lawyer')}:{' '}
                  {contract.assignedLawyerName || t('contractDetail.lawyerAssigned')}
                </span>
              </>
            )}
            {contract.reviewRound > 0 && (
              <span className="flex items-center gap-1.5 font-medium text-foreground">
                {t('contractDetail.reviewRound', { round: contract.reviewRound })}
              </span>
            )}
          </div>

          {/* In review banner — visible to creator, owner, admin */}
          {contract.status === 'IN_REVIEW' && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-violet-300 bg-violet-50 px-4 py-3 dark:border-violet-700 dark:bg-violet-950/30">
                <div className="flex items-start gap-3">
                  <Clock className="size-5 shrink-0 text-violet-600 dark:text-violet-400" />
                  <div>
                    <p className="text-sm font-medium text-violet-800 dark:text-violet-200">
                      {t('contractDetail.inReviewTitle')}
                    </p>
                    <p className="mt-1 text-sm text-violet-700 dark:text-violet-300">
                      {t(
                        isCreator
                          ? 'contractDetail.inReviewDescCreator'
                          : isAssignedLawyer
                            ? 'contractDetail.inReviewDescLawyer'
                            : 'contractDetail.inReviewDescAdmin',
                        {
                          lawyer: contract.assignedLawyerName || t('contractDetail.lawyerAssigned'),
                        },
                      )}
                    </p>
                    {isCreator && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate({
                            to: '/contracts/$contractId/review',
                            params: { contractId: contract.id },
                          })
                        }
                        className="mt-3 cursor-pointer border-violet-300 text-violet-800 hover:bg-violet-100 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-900/50"
                      >
                        {t('contractDetail.openReviewPanel')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Changes requested banner — creator sees action button */}
          {contract.status === 'CHANGES_REQUESTED' && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700 dark:bg-amber-950/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {t(
                        isCreator
                          ? 'contractDetail.changesRequestedTitleCreator'
                          : isAssignedLawyer
                            ? 'contractDetail.changesRequestedTitleLawyer'
                            : 'contractDetail.changesRequestedTitleAdmin',
                      )}
                    </p>
                    <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                      {t(
                        isCreator
                          ? 'contractDetail.changesRequestedDescCreator'
                          : isAssignedLawyer
                            ? 'contractDetail.changesRequestedDescLawyer'
                            : 'contractDetail.changesRequestedDescAdmin',
                      )}
                    </p>
                    {(isCreator || isAssignedLawyer) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate({
                            to: '/contracts/$contractId/review',
                            params: { contractId: contract.id },
                          })
                        }
                        className="mt-3 cursor-pointer border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/50"
                      >
                        {isCreator
                          ? t('contractDetail.openChangesPanel')
                          : t('contractDetail.openReviewPanel')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Cancellation banner — inside card */}
          {contract.status === 'CANCELLED' && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 dark:border-gray-600 dark:bg-gray-800/50">
                <div className="flex items-center gap-3">
                  <Ban className="size-5 shrink-0 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t('contractDetail.cancelledTitle')}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('contractDetail.cancelledDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Rejection banner — inside card */}
          {contract.status === 'REJECTED' && contract.rejectionCategory && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 dark:border-red-700 dark:bg-red-950/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="size-5 shrink-0 text-red-600 dark:text-red-400" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('contractDetail.rejectionTitle')}
                    </p>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                      {t(`contractDetail.reject.${contract.rejectionCategory}`)}
                      {contract.rejectionReason && ` — ${contract.rejectionReason}`}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pending approval banner — creator confirms or returns */}
          {contract.status === 'PENDING_APPROVAL' && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-teal-300 bg-teal-50 px-4 py-3 dark:border-teal-700 dark:bg-teal-950/30">
                <div className="flex items-start gap-3">
                  <Clock className="size-5 shrink-0 text-teal-600 dark:text-teal-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-teal-800 dark:text-teal-200">
                      {t(
                        isCreator
                          ? 'contractDetail.pendingApprovalTitleCreator'
                          : isAssignedLawyer
                            ? 'contractDetail.pendingApprovalTitleLawyer'
                            : 'contractDetail.pendingApprovalTitleAdmin',
                      )}
                    </p>
                    <p className="mt-1 text-sm text-teal-700 dark:text-teal-300">
                      {t(
                        isCreator
                          ? 'contractDetail.pendingApprovalDescCreator'
                          : 'contractDetail.pendingApprovalDescLawyer',
                      )}
                    </p>
                    {isCreator && (
                      <div className="mt-3 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate({
                              to: '/contracts/$contractId/review',
                              params: { contractId: contract.id },
                            })
                          }
                          className="cursor-pointer border-teal-300 text-teal-800 hover:bg-teal-100 dark:border-teal-700 dark:text-teal-200 dark:hover:bg-teal-900/50"
                        >
                          {t('contractDetail.pendingApprovalViewFinal')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Approval banner — inside card */}
          {contract.status === 'APPROVED' && contract.approvedAt && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 dark:border-green-700 dark:bg-green-950/30">
                <div className="flex items-start gap-3">
                  <CheckCircle className="size-5 shrink-0 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      {t('contractDetail.approvedTitle')}
                    </p>
                    <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                      {new Date(contract.approvedAt).toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Generation failed banner */}
          {contract.status === 'GENERATION_FAILED' && (
            <>
              <hr className="my-4 border-border" />
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 dark:bg-destructive/10">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="size-5 shrink-0 text-destructive" />
                  <div>
                    <p className="text-sm font-medium text-destructive">
                      {t('contractDetail.generationFailedBanner')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('contractDetail.generationFailedBannerDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Separator + Workflow actions + Export (hidden in GENERATING/DRAFT_SELECTION) */}
          {contract.status !== 'DRAFT_SELECTION' && contract.status !== 'GENERATING' && (
            <>
              <hr className="my-4 border-border" />
              <WorkflowActionBar
                contract={contract}
                currentUserId={currentUserId}
                currentUserRole={currentUserRole}
                workspaceSuspended={workspaceSuspended}
                onNavigateToEdit={isEditable ? handleNavigateToEdit : undefined}
                onDelete={() => {
                  deleteContractMutation.mutate(contract.id, {
                    onSuccess: () => {
                      toast.success(t('contractsList.deleteSuccess'));
                      void navigate({ to: '/contracts', search: { status: undefined } });
                    },
                    onError: () => toast.error(t('contractsList.deleteError')),
                  });
                }}
                isDeleting={deleteContractMutation.isPending}
                renderSecondaryActions={
                  contract.status !== 'DRAFT_SELECTION' ? (
                    <ExportButton
                      contract={contract}
                      currentUserId={currentUserId}
                      currentUserRole={currentUserRole}
                      latestVersionNumber={latestTimelineVersion?.versionNumber}
                    />
                  ) : undefined
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Generation progress — visible only in GENERATING state */}
      {contract.status === 'GENERATING' && (
        <div className="mb-8">
          <GenerationProgress generatingStartedAt={contract.updatedAt} contractId={contract.id} />
        </div>
      )}

      {/* Draft selection UI — visible only in DRAFT_SELECTION state */}
      {contract.status === 'DRAFT_SELECTION' && (
        <div className="mb-8">
          {contract.drafts && contract.drafts.length > 0 ? (
            <DraftSelection
              contract={contract}
              drafts={contract.drafts}
              isCreator={isCreator}
              onPreviewVersion={(versionId) => setPreviewVersionId(versionId)}
              onRegenerate={() => workflowTransition.execute('regenerate')}
              isRegenerating={workflowTransition.isPending}
              onNavigateToEdit={handleNavigateToEdit}
            />
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                <Loader2 className="mr-2 size-4 animate-spin" />
                {t('common.loading')}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* TODO v2: AI validation warnings (GenerationWarnings) — temporarily hidden */}

      {/* Version timeline — visible from DRAFT onwards (when versions exist) */}
      {!['FORM_FILLING', 'GENERATING', 'GENERATION_FAILED', 'DRAFT_SELECTION'].includes(
        contract.status,
      ) && (
        <div className="mb-8">
          <VersionTimeline
            contractId={contract.id}
            userRole={(currentUserRole as 'OWNER' | 'ADMIN' | 'MEMBER' | 'LAWYER') || 'MEMBER'}
          />
        </div>
      )}

      {/* Contract content CTA — visible from DRAFT onwards */}
      {contract.generatedContent &&
        contract.generatedContent.length > 0 &&
        currentVersion &&
        contract.status !== 'DRAFT_SELECTION' &&
        contract.status !== 'GENERATING' && (
          <div className="mb-8">
            <Card>
              <CardContent className="flex items-center gap-4 py-5">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="size-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{t('contractDetail.contentTitle')}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t('contractDetail.contentDescription', {
                      sections: contract.generatedContent.length,
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewVersionId(currentVersion.id)}
                    className="cursor-pointer"
                  >
                    <Eye className="size-4" />
                    {t('contractDetail.contentPreview')}
                  </Button>
                  {contract.status === 'DRAFT' && isCreator && (
                    <Button
                      onClick={() =>
                        navigate({
                          to: '/contracts/$contractId/review',
                          params: { contractId: contract.id },
                        })
                      }
                      className="cursor-pointer"
                    >
                      <Pencil className="size-4" />
                      {t('contractDetail.contentEdit')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {/* Version preview overlay */}
      {previewVersionId && (previewVersion || currentVersion) && (
        <VersionPreview
          contractId={contract.id}
          versionId={previewVersionId}
          versionNumber={previewVersion?.versionNumber ?? currentVersion?.versionNumber ?? 1}
          isLocked={previewVersion?.isLocked ?? currentVersion?.isLocked ?? false}
          onClose={() => setPreviewVersionId(null)}
          onNavigate={(id) => setPreviewVersionId(id)}
          onDiffWithPrevious={() => {
            if (previewIdx >= 0 && previewIdx < allVersions.length - 1) {
              const previous = allVersions[previewIdx + 1];
              setDiffState({ versionA: previous.id, versionB: previewVersionId! });
              setPreviewVersionId(null);
            }
          }}
          hasPrev={previewIdx >= 0 && previewIdx < allVersions.length - 1}
          hasNext={previewIdx > 0}
          prevId={
            previewIdx >= 0 && previewIdx < allVersions.length - 1
              ? allVersions[previewIdx + 1].id
              : undefined
          }
          nextId={previewIdx > 0 ? allVersions[previewIdx - 1].id : undefined}
          displayPosition={
            contract.status === 'DRAFT_SELECTION'
              ? contract.drafts?.find((d) => d.versionId === previewVersionId)?.displayPosition
              : undefined
          }
          onSelectDraft={
            contract.status === 'DRAFT_SELECTION' && isCreator
              ? (versionId) => {
                  selectDraft.mutate(versionId, {
                    onSuccess: () => {
                      setPreviewVersionId(null);
                      void navigate({
                        to: '/contracts/$contractId',
                        params: { contractId: contract.id },
                      });
                    },
                  });
                }
              : undefined
          }
          isSelectingDraft={selectDraft.isPending}
        />
      )}

      {/* Export history — visible for all states with generated content */}
      {!['FORM_FILLING', 'GENERATING', 'GENERATION_FAILED', 'DRAFT_SELECTION'].includes(
        contract.status,
      ) && (
        <div className="mb-8">
          <ExportHistory contractId={contract.id} />
        </div>
      )}

      {/* Form data preview */}
      <ContractFormPreview
        contract={contract}
        canEdit={isEditable}
        onEdit={isEditable ? handleNavigateToEdit : undefined}
      />

      {/* AI Act banner — visible for DRAFT and later states (AI-generated content) */}
      {contract.status !== 'FORM_FILLING' && contract.status !== 'CANCELLED' && (
        <div className="mt-8 rounded-md border border-muted px-3 py-2.5">
          <div className="flex items-start gap-2.5">
            <Info className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {t('contractDetail.aiActBanner')}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                {t('contractDetail.aiActBannerDesc')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Diff viewer overlay — triggered from version preview */}
      {diffState && (
        <ContractDiffOverlay
          contractId={contract.id}
          versionA={diffState.versionA}
          versionB={diffState.versionB}
          onClose={() => setDiffState(null)}
        />
      )}
    </div>
  );
}

function ContractDiffOverlay({
  contractId,
  versionA,
  versionB,
  onClose,
}: {
  contractId: string;
  versionA: string;
  versionB: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: diff, isLoading, error } = useDiff(contractId, versionA, versionB);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !diff) {
    return (
      <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm">
        <p className="text-sm text-destructive">{t('timeline.diffError')}</p>
        <Button variant="outline" size="sm" onClick={onClose} className="cursor-pointer">
          {t('diff.close')}
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] overflow-auto bg-background">
      <DiffViewer diff={diff} onClose={onClose} />
    </div>
  );
}
